import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import API_CONFIG from "../../config/api";
import {
  ShipmentDocument,
  ShipmentDocumentType,
} from "../types/shipment.types";
import { useShipments } from "../hooks/useShipments";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";

import useIsMobile from "../../hooks/useIsMobile";
import { ShipmentMetricStrip, ShipmentTabHeading } from "./ShipmentDetailLayout";

const { TextArea } = Input;
const { Text } = Typography;

type DocumentTypeMeta = {
  label: string;
  category: string;
  color: string;
};

const DOCUMENT_TYPES: Record<ShipmentDocumentType, DocumentTypeMeta> = {
  bl: { label: "BL (Bill of Lading)", category: "Transport", color: "blue" },
  lta: { label: "LTA / AWB", category: "Transport", color: "blue" },
  cmr: { label: "CMR (Route)", category: "Transport", color: "blue" },
  commercial_invoice: {
    label: "Facture commerciale",
    category: "Facturation",
    color: "gold",
  },
  packing_list: {
    label: "Packing list",
    category: "Marchandise",
    color: "green",
  },
  certificate_origin: {
    label: "Certificat d'origine",
    category: "Douane",
    color: "geekblue",
  },
  customs_declaration: {
    label: "Declaration en douane",
    category: "Douane",
    color: "geekblue",
  },
  insurance: {
    label: "Police d'assurance",
    category: "Assurance",
    color: "volcano",
  },
  other: { label: "Autre document", category: "Autre", color: "default" },
};

const CATEGORY_COLORS: Record<string, string> = {
  Transport: "blue",
  Douane: "geekblue",
  Facturation: "gold",
  Marchandise: "green",
  Assurance: "volcano",
  Autre: "default",
};

const DOCUMENT_TYPE_VALUES = new Set(Object.keys(DOCUMENT_TYPES));

const createLocalId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `doc_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
};

const getFileNameFromUrl = (url: string) => {
  const sanitizedUrl = url.split("?")[0];
  const segments = sanitizedUrl.split("/");
  return segments[segments.length - 1] || "document";
};

const extractKeyFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    const path = parsedUrl.pathname.replace(/^\/+/, "");
    return path || null;
  } catch (error) {
    const sanitizedUrl = url.split("?")[0];
    const trimmed = sanitizedUrl.replace(/^\/+/, "");
    return trimmed || null;
  }
};

const encodeBase64Url = (value: string) => {
  const base64 = typeof btoa === "function" ? btoa(value) : value;
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const normalizeDocumentType = (value: unknown): ShipmentDocumentType => {
  if (typeof value === "string" && DOCUMENT_TYPE_VALUES.has(value)) {
    return value as ShipmentDocumentType;
  }
  return "other";
};

const normalizeAttachments = (
  attachments: ShipmentDocument[] | string | null | undefined
): ShipmentDocument[] => {
  if (!attachments) {
    return [];
  }

  let raw: unknown = attachments;
  if (typeof attachments === "string") {
    try {
      raw = JSON.parse(attachments);
    } catch (error) {
      console.warn("[LOGISTICS] Unable to parse attachments JSON", error);
      return [];
    }
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((item) => {
    if (typeof item === "string") {
      const fileKey = extractKeyFromUrl(item) || undefined;
      return {
        id: fileKey ? `legacy:${encodeBase64Url(fileKey)}` : createLocalId(),
        type: "other",
        file: { url: item, name: getFileNameFromUrl(item), key: fileKey },
      } as ShipmentDocument;
    }

    const candidate = item as ShipmentDocument & {
      url?: string;
      name?: string;
      key?: string;
      file_key?: string;
    };
    const fileUrl = candidate.file?.url || candidate.url;
    const fileKey =
      candidate.file?.key ||
      candidate.key ||
      candidate.file_key ||
      (fileUrl ? extractKeyFromUrl(fileUrl) : undefined);
    const fileName =
      candidate.file?.name ||
      candidate.name ||
      (fileUrl ? getFileNameFromUrl(fileUrl) : undefined);
    const file =
      fileUrl || fileKey
        ? {
            url: fileUrl,
            name: fileName,
            key: fileKey,
            mimetype: candidate.file?.mimetype,
            size: candidate.file?.size,
          }
        : undefined;

    return {
      id:
        candidate.id ||
        (fileKey ? `legacy:${encodeBase64Url(fileKey)}` : createLocalId()),
      type: normalizeDocumentType(candidate.type),
      label: candidate.label,
      reference: candidate.reference,
      document_date: candidate.document_date,
      notes: candidate.notes,
      file,
      uploaded_at: candidate.uploaded_at,
      uploaded_by: candidate.uploaded_by,
    } as ShipmentDocument;
  });
};

interface ShipmentDocumentsTabProps {
  shipmentId: number;
  attachments?: ShipmentDocument[] | string | null;
  canEdit?: boolean;
}

interface UploadedFileInfo {
  url: string;
  key: string;
  filename: string;
  mimetype: string;
  size: number;
}

const ShipmentDocumentsTab: React.FC<ShipmentDocumentsTabProps> = ({
  shipmentId,
  attachments,
  canEdit,
}) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const { updateShipment } = useShipments();
  const permissions = useLogisticsPermissions();
  const { user } = useAuth();
  const { selectedCompany } = useSelection();

  const [documents, setDocuments] = useState<ShipmentDocument[]>([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFileInfo | null>(
    null
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEditable =
    canEdit !== undefined
      ? canEdit
      : permissions.canUpdateShipmentStatus || permissions.canCreateShipments;

  useEffect(() => {
    setDocuments(normalizeAttachments(attachments));
  }, [attachments]);

  const getProxyId = (doc: ShipmentDocument) => {
    if (doc.id) {
      return doc.id;
    }
    const key = doc.file?.key || (doc.file?.url ? extractKeyFromUrl(doc.file.url) : null);
    if (!key) {
      return null;
    }
    return `legacy:${encodeBase64Url(key)}`;
  };

  const buildProxyUrl = (doc: ShipmentDocument) => {
    const proxyId = getProxyId(doc);
    if (!proxyId) {
      return null;
    }
    const encodedId = encodeURIComponent(proxyId);
    const baseUrl = `${API_CONFIG.API_URL}/logistics/shipments/${shipmentId}/documents/${encodedId}`;
    if (selectedCompany) {
      return `${baseUrl}?company_id=${encodeURIComponent(String(selectedCompany))}`;
    }
    return baseUrl;
  };

  const openDocument = async (doc: ShipmentDocument) => {
    const proxyUrl = buildProxyUrl(doc);
    if (!proxyUrl) {
      message.warning("Fichier indisponible");
      return;
    }

    try {
      const response = await axios.get(proxyUrl, {
        responseType: "blob",
      });
      const contentType =
        response.headers["content-type"] ||
        doc.file?.mimetype ||
        "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      console.error("[LOGISTICS] Unable to open document:", error);
      message.error("Impossible d'ouvrir le document");
    }
  };

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    documents.forEach((doc) => {
      const meta = DOCUMENT_TYPES[doc.type] || DOCUMENT_TYPES.other;
      counts[meta.category] = (counts[meta.category] || 0) + 1;
    });
    return counts;
  }, [documents]);

  const handleOpenAddModal = () => {
    form.resetFields();
    setUploadedFile(null);
    setAddModalVisible(true);
  };

  const deleteUploadedFile = async (fileKey: string) => {
    try {
      await axios.delete(
        `${API_CONFIG.API_URL}/logistics/shipments/documents/${encodeURIComponent(
          fileKey
        )}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          params: selectedCompany ? { company_id: selectedCompany } : {},
        }
      );
      return true;
    } catch (error) {
      console.warn("[LOGISTICS] Unable to delete temp file", error);
      return false;
    }
  };

  const handleCloseAddModal = async (cleanup = true) => {
    if (cleanup && uploadedFile?.key) {
      await deleteUploadedFile(uploadedFile.key);
    }
    setAddModalVisible(false);
    setUploadedFile(null);
    form.resetFields();
  };

  const beforeUpload = (file: File) => {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      message.error("Formats autorises: PDF, JPEG, PNG");
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("Le fichier doit faire moins de 10MB");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("document", file);
      if (selectedCompany) {
        formData.append("company_id", String(selectedCompany));
      }

      const response = await axios.post(
        `${API_CONFIG.API_URL}/logistics/shipments/upload-document`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress({ percent });
            }
          },
        }
      );

      if (response.data.success) {
        setUploadedFile({
          url: response.data.url,
          key: response.data.key,
          filename: response.data.filename,
          mimetype: response.data.mimetype,
          size: response.data.size,
        });
        message.success(`${file.name} upload termine`);
        onSuccess(response.data, file);
      } else {
        throw new Error(response.data.message || "Erreur upload");
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Upload error:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de l'upload du document"
      );
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveUpload = async () => {
    if (uploadedFile?.key) {
      await deleteUploadedFile(uploadedFile.key);
    }
    setUploadedFile(null);
  };

  const updateAttachments = async (nextDocuments: ShipmentDocument[]) => {
    const success = await updateShipment(shipmentId, {
      attachments: nextDocuments,
    });
    if (success) {
      setDocuments(nextDocuments);
    }
    return success;
  };

  const handleAddDocument = async () => {
    try {
      const values = await form.validateFields();
      if (!uploadedFile) {
        message.warning("Veuillez uploader un fichier");
        return;
      }

      const newDocument: ShipmentDocument = {
        id: createLocalId(),
        type: normalizeDocumentType(values.type),
        label: values.label || undefined,
        reference: values.reference || undefined,
        document_date: values.document_date
          ? values.document_date.format("YYYY-MM-DD")
          : undefined,
        notes: values.notes || undefined,
        file: {
          url: uploadedFile.url,
          key: uploadedFile.key,
          name: uploadedFile.filename,
          mimetype: uploadedFile.mimetype,
          size: uploadedFile.size,
        },
        uploaded_at: new Date().toISOString(),
        uploaded_by: {
          id: user?.id,
          name: user?.name,
        },
      };

      const nextDocuments = [...documents, newDocument];
      setSaving(true);
      const success = await updateAttachments(nextDocuments);
      if (success) {
        await handleCloseAddModal(false);
      }
    } catch (error) {
      console.error("[LOGISTICS] Validation error:", error);
      message.error("Veuillez completer les champs requis");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (doc: ShipmentDocument) => {
    const fileKey =
      doc.file?.key || (doc.file?.url ? extractKeyFromUrl(doc.file.url) : null);
    if (fileKey) {
      try {
        await axios.delete(
          `${API_CONFIG.API_URL}/logistics/shipments/documents/${encodeURIComponent(
            fileKey
          )}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            params: selectedCompany ? { company_id: selectedCompany } : {},
          }
        );
      } catch (error: any) {
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la suppression du fichier"
        );
        return;
      }
    }

    const nextDocuments = documents.filter((item) => item.id !== doc.id);
    setSaving(true);
    await updateAttachments(nextDocuments);
    setSaving(false);
  };

  const columns = [
    {
      title: "Type",
      key: "type",
      render: (_: unknown, record: ShipmentDocument) => {
        const meta = DOCUMENT_TYPES[record.type] || DOCUMENT_TYPES.other;
        const label = record.label || meta.label;
        return (
          <Space direction="vertical" size={0}>
            <strong>{label}</strong>
            <Tag color={meta.color}>{meta.category}</Tag>
          </Space>
        );
      },
    },
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      render: (value: string) => value || "-",
    },
    {
      title: "Date",
      dataIndex: "document_date",
      key: "document_date",
      render: (value: string | undefined) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Fichier",
      key: "file",
      render: (_: unknown, record: ShipmentDocument) => {
        const hasFile = Boolean(record.file?.url || record.file?.key);
        if (!hasFile) {
          return <Tag>Aucun fichier</Tag>;
        }
        return (
          <Button type="link" onClick={() => openDocument(record)}>
            <Space>
              <FileTextOutlined aria-hidden="true" />
              <span>{record.file?.name || "Document"}</span>
            </Space>
          </Button>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (value: string) =>
        value ? (
          <Tooltip title={value}>{value}</Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Ajouté par",
      key: "uploaded_by",
      render: (_: unknown, record: ShipmentDocument) =>
        record.uploaded_by?.name || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: unknown, record: ShipmentDocument) => (
        <Space>
          {(record.file?.url || record.file?.key) && (
            <Tooltip title="Voir">
              <Button
                size="small"
                aria-label={`Ouvrir ${record.file?.name || "le document"}`} icon={<EyeOutlined aria-hidden="true" />}
                onClick={() => openDocument(record)}
              />
            </Tooltip>
          )}
          {isEditable && (
            <Popconfirm
              title="Supprimer ce document ?"
              onConfirm={() => handleDeleteDocument(record)}
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                danger
                aria-label={`Supprimer ${record.file?.name || record.reference || "le document"}`} icon={<DeleteOutlined aria-hidden="true" />}
                loading={saving}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="shipment-panel shipment-documents">
      <ShipmentTabHeading title="Documents du dossier" icon={<FileTextOutlined aria-hidden="true" />}
        extra={isEditable && <Button type="primary" icon={<PlusOutlined aria-hidden="true" />} onClick={handleOpenAddModal}>Ajouter un document</Button>} />
      <div className="shipment-document-summary">
        <ShipmentMetricStrip items={[{ label: "Total documents", value: documents.length },
          ...Object.keys(CATEGORY_COLORS).filter(category => (categoryCounts[category] || 0) > 0).map(category => ({ label: category, value: categoryCounts[category] }))]} />
      </div>
      <Card className="shipment-table-panel">
        {documents.length === 0 ? (
          <Empty description="Aucun document enregistre" />
        ) : (
          <>
            {isMobile ? (
              <List
                dataSource={documents}
                pagination={{ pageSize: 6, size: "small" }}
                renderItem={(record) => {
                  const meta = DOCUMENT_TYPES[record.type] || DOCUMENT_TYPES.other;
                  const label = record.label || meta.label;
                  const hasFile = Boolean(record.file?.url || record.file?.key);

                  return (
                    <List.Item style={{ padding: 0, border: "none" }}>
                      <Card size="small" style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 12,
                          }}
                        >
                          <Space direction="vertical" size={0}>
                            <Tag color={meta.color}>{label}</Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {meta.category}
                            </Text>
                          </Space>

                          <Space size="small">
                            {hasFile && (
                              <Tooltip title="Voir">
                                <Button
                                  size="small"
                                  aria-label={`Ouvrir ${record.file?.name || "le document"}`}
                                  icon={<EyeOutlined aria-hidden="true" />}
                                  onClick={() => openDocument(record)}
                                />
                              </Tooltip>
                            )}
                            {isEditable && (
                              <Popconfirm
                                title="Supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(record)}
                                okText="Supprimer"
                                cancelText="Annuler"
                                okButtonProps={{ danger: true }}
                              >
                                <Button
                                  size="small"
                                  danger
                                  aria-label={`Supprimer ${record.file?.name || record.reference || "le document"}`}
                                  icon={<DeleteOutlined aria-hidden="true" />}
                                  loading={saving}
                                />
                              </Popconfirm>
                            )}
                          </Space>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <Space direction="vertical" size={6} style={{ width: "100%" }}>
                            <div>
                              <Text type="secondary">Référence : </Text>
                              <Text>{record.reference || "-"}</Text>
                            </div>

                            <div>
                              <Text type="secondary">Date : </Text>
                              <Text>
                                {record.document_date
                                  ? dayjs(record.document_date).format("DD/MM/YYYY")
                                  : "-"}
                              </Text>
                            </div>

                            <div>
                              <Text type="secondary">Fichier : </Text>
                              {hasFile ? (
                                <Button
                                  type="link"
                                  onClick={() => openDocument(record)}
                                  style={{ padding: 0, height: "auto" }}
                                >
                                  <Space size={6}>
                                    <FileTextOutlined aria-hidden="true" />
                                    <span>{record.file?.name || "Document"}</span>
                                  </Space>
                                </Button>
                              ) : (
                                <Tag>Aucun fichier</Tag>
                              )}
                            </div>

                            <div>
                              <Text type="secondary">Ajouté par : </Text>
                              <Text>{record.uploaded_by?.name || "-"}</Text>
                            </div>

                            {record.notes && (
                              <div>
                                <Text type="secondary">Notes : </Text>
                                <Text>{record.notes}</Text>
                              </div>
                            )}
                          </Space>
                        </div>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Table
                columns={columns}
                dataSource={documents}
                rowKey="id"
                pagination={{ pageSize: 8, showTotal: total => `${total} documents affichés` }}
                size="middle"
                scroll={{ x: "max-content" }}
              />
            )}
          </>
        )}
      </Card>
      <footer className="shipment-tab-footer">Ouvrez un fichier pour consulter son contenu.</footer>

      <Modal
        title="Ajouter un document"
        open={addModalVisible}
        onCancel={() => {
          void handleCloseAddModal();
        }}
        onOk={handleAddDocument}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={saving}
        className={isMobile ? "mobile-fullscreen-modal" : undefined}
        centered={!isMobile}
        width={isMobile ? "100vw" : 600}
        style={
          isMobile ? { top: 0, paddingBottom: 0, margin: 0, maxWidth: "100vw" } : undefined
        }
        styles={
          isMobile
            ? {
                content: { borderRadius: 0 },
                body: {
                  maxHeight: "calc(100vh - 120px)",
                  overflowY: "auto",
                  padding: 12,
                },
              }
            : {
                body: {
                  maxHeight: "calc(100vh - 200px)",
                  overflowY: "auto",
                },
              }
        }
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Type de document"
            name="type"
            rules={[{ required: true, message: "Type requis" }]}
          >
            <Select placeholder="Selectionner un type">
              {Object.entries(DOCUMENT_TYPES).map(([value, meta]) => (
                <Select.Option key={value} value={value}>
                  {meta.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) =>
              getFieldValue("type") === "other" ? (
                <Form.Item
                  label="Libelle"
                  name="label"
                  rules={[{ required: true, message: "Libelle requis" }]}
                >
                  <Input placeholder="Ex: Certificat phytosanitaire" />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item label="Reference" name="reference">
            <Input placeholder="Numero ou reference interne" />
          </Form.Item>

          <Form.Item label="Date du document" name="document_date">
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="Selectionner la date"
            />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="Notes internes ou complements..."
            />
          </Form.Item>

          <Form.Item
            label="Fichier"
            required
            tooltip="PDF, JPEG, PNG - max 10MB"
          >
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              maxCount={1}
              beforeUpload={beforeUpload}
              customRequest={customRequest}
              onRemove={handleRemoveUpload}
              fileList={
                uploadedFile
                  ? [
                      {
                        uid: "1",
                        name: uploadedFile.filename,
                        status: "done",
                        url: uploadedFile.url,
                      },
                    ]
                  : []
              }
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
                showDownloadIcon: false,
              }}
            >
              {!uploadedFile && (
                <Button icon={<UploadOutlined aria-hidden="true" />} loading={uploading}>
                  {uploading ? "Upload en cours..." : "Choisir un fichier"}
                </Button>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShipmentDocumentsTab;
